import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Form,
  message,
  Modal,
  Spin,
  Tooltip,
  Upload,
  Image,
  Card,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
  EditOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { confirm } = Modal;

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const placeholderImage = "/category-images/placeholder.jpg";

  const formatCategory = (cat) => ({
    key: String(cat.id),
    id: cat.id,
    name: cat.category_name,
    imageUrl: cat.image_url || "",
  });

  const fetchCategories = async () => {
    setLoading(true);

    try {
      const response = await axiosInstance.get("/category/categories");

      if (response.data.success) {
        setCategories(response.data.categories.map(formatCategory));
      } else {
        message.error("Failed to fetch categories.");
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "An error occurred while fetching categories."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const normFile = (e) => {
    if (Array.isArray(e)) return e;
    return e?.fileList || [];
  };

  const getFileFromUpload = (fileList) => {
    if (!Array.isArray(fileList) || fileList.length === 0) return null;
    return fileList[0]?.originFileObj || null;
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("Only image files are allowed.");
        return Upload.LIST_IGNORE;
      }

      const isLt5MB = file.size / 1024 / 1024 < 5;
      if (!isLt5MB) {
        message.error("Image must be smaller than 5MB.");
        return Upload.LIST_IGNORE;
      }

      return false;
    },
    maxCount: 1,
    accept: "image/*",
    listType: "picture",
  };

  const handleAddCategory = async (values) => {
    try {
      const categoryName = String(values.name || "").trim();

      if (!categoryName) {
        message.error("Please enter category name.");
        return;
      }

      const imageFile = getFileFromUpload(values.image);

      setSubmitting(true);

      const formData = new FormData();
      formData.append("category_name", categoryName);

      if (imageFile) {
        formData.append("image", imageFile);
      }

      const response = await axiosInstance.post("/admin/category", formData);

      if (response.status === 201 && response.data.success) {
        const newCategory = formatCategory(response.data.category);

        setCategories((prev) => [...prev, newCategory]);
        message.success("Category added successfully!");
        setAddModalVisible(false);
        form.resetFields();
      } else {
        message.error("Failed to add category.");
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "An error occurred while adding the category."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (record) => {
    setSelectedCategory(record);
    editForm.setFieldsValue({
      name: record.name,
      image: [],
    });
    setEditModalVisible(true);
  };

  const handleUpdateCategory = async (values) => {
    try {
      if (!selectedCategory?.key) {
        message.error("Category not selected.");
        return;
      }

      const categoryName = String(values.name || "").trim();
      const imageFile = getFileFromUpload(values.image);

      if (!categoryName) {
        message.error("Please enter category name.");
        return;
      }

      const formData = new FormData();

      if (categoryName !== selectedCategory.name) {
        formData.append("category_name", categoryName);
      }

      if (imageFile) {
        formData.append("image", imageFile);
      }

      if (!formData.has("category_name") && !formData.has("image")) {
        message.warning("No changes selected.");
        return;
      }

      setSubmitting(true);

      const response = await axiosInstance.put(
        `/admin/category/${selectedCategory.key}`,
        formData
      );

      if (response.data.success) {
        const updatedCategory = formatCategory(response.data.category);

        setCategories((prev) =>
          prev.map((cat) =>
            cat.key === selectedCategory.key ? updatedCategory : cat
          )
        );

        message.success("Category updated successfully!");
        setEditModalVisible(false);
        setSelectedCategory(null);
        editForm.resetFields();
      } else {
        message.error("Failed to update category.");
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "An error occurred while updating category."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = (key) => {
    confirm({
      title: "Are you sure you want to delete this category?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          const response = await axiosInstance.delete(`/admin/category/${key}`);

          if (response.data.success) {
            setCategories((prev) => prev.filter((cat) => cat.key !== key));
            message.success("Category deleted successfully!");
          } else {
            message.error("Failed to delete category.");
          }
        } catch (error) {
          message.error(
            error?.response?.data?.message ||
              "An error occurred while deleting the category."
          );
        }
      },
    });
  };

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: "Sr.No",
      key: "srno",
      width: 80,
      render: (_, record, index) => (
        <div className="text-center font-semibold text-gray-600">
          {index + 1}
        </div>
      ),
    },
    {
      title: "Image",
      dataIndex: "imageUrl",
      key: "imageUrl",
      width: 120,
      align: "center",
      render: (imageUrl, record) => (
        <div className="flex justify-center">
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={record.name}
              width={68}
              height={52}
              className="rounded-xl object-cover border border-gray-200 shadow-sm"
              fallback={placeholderImage}
              preview
            />
          ) : (
            <div className="w-[68px] h-[52px] rounded-xl border border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center text-[10px] text-gray-400">
              <PictureOutlined className="text-base mb-1" />
              No Image
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
      align: "left",
      render: (name) => (
        <div className="text-sm font-semibold text-gray-900">{name}</div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      align: "center",
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2">
          <Tooltip title="Edit Category">
            <Button
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
              size="small"
              className="border-gray-300"
            />
          </Tooltip>

          <Tooltip title="Delete Category">
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteCategory(record.key)}
              size="small"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <Card className="rounded-2xl shadow-sm border border-gray-200 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                  Manage Categories
                </h1>
                <p className="text-gray-600 mb-0">
                  Add category name and upload category image from admin panel.
                </p>
              </div>

              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddModalVisible(true)}
                className="h-10 bg-blue-600 hover:bg-blue-700 border-blue-600 rounded-lg"
              >
                Add New Category
              </Button>
            </div>
          </Card>

          <Card className="rounded-2xl shadow-sm border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <SearchOutlined className="mr-2" />
                  Search Categories
                </label>
                <Input
                  placeholder="Search category name..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full h-10 rounded-lg"
                  allowClear
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">
                  Category Summary
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 px-4 h-10 flex items-center">
                  <div className="text-sm font-semibold text-gray-900">
                    Total: {filteredCategories.length} categories
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card className="rounded-2xl shadow-sm border border-gray-200 overflow-hidden p-0">
            <Table
              dataSource={filteredCategories}
              columns={columns}
              rowKey="key"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                pageSizeOptions: ["10", "20", "50"],
              }}
              scroll={{ x: "max-content" }}
              className="modern-orders-table"
              rowClassName={() =>
                "hover:bg-gray-50 transition-colors duration-200"
              }
              locale={{
                emptyText: (
                  <div className="text-center py-8">
                    <div className="text-lg font-medium text-gray-500 mb-2">
                      No categories found
                    </div>
                    <div className="text-sm text-gray-400">
                      Try adding a new category or adjusting your search
                    </div>
                  </div>
                ),
              }}
            />
          </Card>
        </div>
      </div>

      <Modal
        title={
          <div className="flex items-center gap-2">
            <PlusOutlined className="text-blue-600" />
            <span className="font-semibold">Add New Category</span>
          </div>
        }
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={560}
        className="modern-modal"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleAddCategory}>
          <Form.Item
            label="Category Name"
            name="name"
            rules={[
              { required: true, message: "Please enter a category name!" },
              {
                validator: (_, value) => {
                  if (!value || String(value).trim()) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Category name cannot be empty spaces.")
                  );
                },
              },
            ]}
          >
            <Input
              placeholder="Enter category name"
              size="large"
              className="rounded-lg h-12"
            />
          </Form.Item>

          <Form.Item
            label="Category Image"
            name="image"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
          </Form.Item>

          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 mb-5 text-sm text-blue-700">
            Image is optional. If no image is uploaded, frontend fallback image
            will be used.
          </div>

          <Form.Item className="mb-0">
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => {
                  setAddModalVisible(false);
                  form.resetFields();
                }}
                className="rounded-lg"
              >
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                className="bg-blue-600 hover:bg-blue-700 border-blue-600 rounded-lg"
              >
                Add Category
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <div className="flex items-center gap-2">
            <EditOutlined className="text-blue-600" />
            <span className="font-semibold">Edit Category</span>
          </div>
        }
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedCategory(null);
          editForm.resetFields();
        }}
        footer={null}
        width={560}
        className="modern-modal"
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateCategory}
        >
          <Form.Item
            label="Category Name"
            name="name"
            rules={[
              { required: true, message: "Please enter a category name!" },
              {
                validator: (_, value) => {
                  if (!value || String(value).trim()) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Category name cannot be empty spaces.")
                  );
                },
              },
            ]}
          >
            <Input
              placeholder="Enter category name"
              size="large"
              className="rounded-lg h-12"
            />
          </Form.Item>

          {selectedCategory?.imageUrl ? (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Current Image
              </div>
              <Image
                src={selectedCategory.imageUrl}
                width={100}
                height={75}
                className="rounded-xl object-cover border border-gray-200"
                fallback={placeholderImage}
              />
            </div>
          ) : null}

          <Form.Item
            label="Upload New Image"
            name="image"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>Upload New Image</Button>
            </Upload>
          </Form.Item>

          <Form.Item className="mb-0">
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  setSelectedCategory(null);
                  editForm.resetFields();
                }}
                className="rounded-lg"
              >
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                className="bg-blue-600 hover:bg-blue-700 border-blue-600 rounded-lg"
              >
                Update Category
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <style jsx>{`
        .modern-orders-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 12px 16px;
        }

        .modern-orders-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 12px 16px;
        }

        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }

        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
          padding: 16px 20px;
        }

        .modern-modal .ant-modal-body {
          padding: 22px 20px;
        }

        .ant-input {
          border-radius: 8px !important;
        }
      `}</style>
    </Spin>
  );
};

export default CategoryManager;